/**
 * The diagnostics control's state behind the titlebar's Help → Package logs
 * item — the result dialog and the failure toast for the bridge call
 * (`lib/diagnostics.ts` holds the pure parts).
 *
 * `available` is decided once per mount from `window.bimgo.diagnostics`: the
 * preload is fixed for the life of the window, so there is nothing to re-check.
 * When it is false the caller renders nothing — the control is absent against
 * an older desktop, never disabled and never stubbed.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocale } from "../i18n/LocaleProvider";
import {
  detectDiagnostics,
  describePackaged,
  runPackageLogs,
  type PackagedMessage,
} from "../lib/diagnostics";
import type { DiagnosticsBridge } from "../bimgo";

export interface Diagnostics {
  /** Whether this desktop exposes the surface at all. */
  available: boolean;
  bridge: DiagnosticsBridge | null;
  /** A `packageLogs` call in flight — the button reads "Packaging…" and refuses a second click. */
  busy: boolean;
  /** The last successful bundle, until dismissed. */
  packaged: PackagedMessage | null;
  /** The last failure, until dismissed or auto-cleared. */
  error: string | null;
  packageLogs: () => Promise<void>;
  dismissPackaged: () => void;
  dismissError: () => void;
}

export function useDiagnostics(): Diagnostics {
  const { t } = useLocale();
  const bridge = useMemo(
    () => detectDiagnostics(typeof window === "undefined" ? undefined : window),
    [],
  );
  const [busy, setBusy] = useState(false);
  const [packaged, setPackaged] = useState<PackagedMessage | null>(null);
  const [error, setError] = useState<string | null>(null);

  // A failure lingers long enough to read, as the plugins pane's toast does.
  useEffect(() => {
    if (!error) return;
    const id = window.setTimeout(() => setError(null), 7000);
    return () => window.clearTimeout(id);
  }, [error]);

  const packageLogs = useCallback(async () => {
    if (!bridge || busy) return;
    setBusy(true);
    setError(null);
    try {
      const outcome = await runPackageLogs(bridge);
      if (outcome.kind === "packaged") setPackaged(describePackaged(outcome.result, t));
      else if (outcome.kind === "error")
        setError(t("settings.diagnostics.failed", { error: outcome.message }));
      // `cancelled`: the user closed the save dialog — nothing to say.
    } finally {
      setBusy(false);
    }
  }, [bridge, busy, t]);

  return {
    available: bridge !== null,
    bridge,
    busy,
    packaged,
    error,
    packageLogs,
    dismissPackaged: useCallback(() => setPackaged(null), []),
    dismissError: useCallback(() => setError(null), []),
  };
}
